import { type Table as TanstackTable, flexRender } from "@tanstack/react-table";
import type * as React from "react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DataTablePagination } from "./data-table-pagination";

interface DataTableProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
	table: TanstackTable<TData>;
	canPreviousPage: boolean;
	canNextPage: boolean;
	floatingBar?: React.ReactNode | null;
}

export function DataTable<TData>({
	table,
	canPreviousPage,
	canNextPage,
	floatingBar = null,
	children,
	className,
	...props
}: DataTableProps<TData>) {

	return (
		<div
			className={cn("w-full space-y-2.5", className)}
			{...props}
		>
			{children}
			<div className="max-h-[calc(100vh-260px)] overflow-y-auto rounded-md border-0 bg-white">
				<Table className="w-full">
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() === Number.MAX_SAFE_INTEGER ? "auto" : header.getSize() }}>
											{header.isPlaceholder
												? null
												: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>

					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											className="overflow-hidden truncate pr-[5px]"
											style={{
												maxWidth: cell.column.getSize() === Number.MAX_SAFE_INTEGER ? "auto" : cell.column.getSize(),
											}}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={table.getAllColumns().length}
									className="h-24 text-center"
								>
									Sin resultados.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div className="flex flex-col gap-2.5">
				<DataTablePagination 
					table={table} 
					canPreviousPage={canPreviousPage}
					canNextPage={canNextPage}
				/>
				{table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
			</div>
		</div>
	);
}